import { Edit, History, Loader2, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import {
  configurationProfileApi,
  type PaginatedResponse,
} from '@/api/production/configuration-profiles';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Pagination } from '@/components/ui/pagination';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useReplaceConfigurationProfile } from '@/hooks/production/use-configuration-profiles';
import { useToast } from '@/hooks/use-toast';
import type {
  IConfigurationProfile,
  IConfigurationProfileHistory,
} from '@/types/production/configuration-profile';
import {
  CONFIGURATION_PROFILE_STATUS_OPTIONS,
  getConfigurationProfileStatus,
  isProfileActive,
} from '@/types/production/configuration-profile-list.type';

interface ConfigurationProfilesTableProps {
  data: PaginatedResponse<IConfigurationProfile> | undefined;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  isLoading?: boolean;
  canViewHistory?: boolean;
}

function getStatusBadgeVariant(status: string) {
  switch (status) {
    case 'ATIVO':
      return 'success-subtle' as const;
    case 'INATIVO':
      return 'secondary-subtle' as const;
    case 'AGUARDANDO_VALIDACAO':
      return 'warning-subtle' as const;
    default:
      return 'secondary-subtle' as const;
  }
}

function formatId(id: string) {
  return id.length > 8 ? `${id.slice(0, 8)}...` : id;
}

function formatDisplayId(profile: IConfigurationProfile) {
  return profile.sequential_id
    ? String(profile.sequential_id).padStart(4, '0')
    : formatId(profile.id);
}

function formatDocument(profile: IConfigurationProfile) {
  return profile.client?.document?.value ?? '--';
}

function formatClient(profile: IConfigurationProfile) {
  if (profile.client) {
    return profile.client.trade_name ?? profile.client.company_name ?? '--';
  }
  return profile.manual_client ?? '--';
}

function formatDate(date: Date | undefined) {
  if (!date) return '--';
  return new Date(date).toLocaleDateString('pt-BR');
}

const SKELETON_ROWS = ['sk-1', 'sk-2', 'sk-3', 'sk-4', 'sk-5'] as const;

function TableSkeleton() {
  return (
    <>
      {SKELETON_ROWS.map((key) => (
        <TableRow key={key}>
          <TableCell>
            <Skeleton className="h-4 w-16" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-32" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-28" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-24" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-20" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-20" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-20" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-5 w-24 rounded-full" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-8 w-44 rounded" />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

export function ConfigurationProfilesTable({
  data,
  currentPage = 1,
  onPageChange,
  isLoading = false,
  canViewHistory = true,
}: ConfigurationProfilesTableProps) {
  const { docs = [], pages = 1, total = 0, limit = 20 } = data || {};
  const { toast } = useToast();
  const [historyProfile, setHistoryProfile] = useState<IConfigurationProfile | null>(null);
  const [historyItems, setHistoryItems] = useState<IConfigurationProfileHistory[]>([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [replaceProfile, setReplaceProfile] = useState<IConfigurationProfile | null>(null);
  const replaceMutation = useReplaceConfigurationProfile();

  const handleHistory = async (profile: IConfigurationProfile) => {
    setHistoryProfile(profile);
    setIsHistoryLoading(true);
    try {
      const history = await configurationProfileApi.findHistory(profile.id);
      setHistoryItems(history.docs);
    } catch {
      setHistoryItems([]);
      toast({
        title: 'Erro ao carregar histórico',
        description: 'Não foi possível consultar o histórico do perfil.',
        variant: 'destructive',
      });
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const confirmReplace = async () => {
    if (!replaceProfile) return;
    try {
      await replaceMutation.mutateAsync(replaceProfile.id);
      setReplaceProfile(null);
      toast({ variant: 'success', title: 'Perfil substituído' });
    } catch {
      toast({
        title: 'Erro ao substituir perfil',
        description: 'Apenas perfis ativos podem ser substituídos.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[100px]">ID</TableHead>
              <TableHead>Nome</TableHead>
              <TableHead>Cliente</TableHead>
              <TableHead>CPF/CNPJ</TableHead>
              <TableHead>Cadastro</TableHead>
              <TableHead>Edição/Validação</TableHead>
              <TableHead>Tecnologia</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[120px] text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableSkeleton />
            ) : docs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="h-24 text-center">
                  <div className="flex flex-col items-center justify-center space-y-2">
                    <p className="text-sm text-muted-foreground">
                      Nenhum perfil de configuração encontrado
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              docs.map((profile) => {
                const status = getConfigurationProfileStatus(profile);
                const statusLabel =
                  CONFIGURATION_PROFILE_STATUS_OPTIONS.find((option) => option.value === status)
                    ?.label ?? status;
                const badgeVariant = getStatusBadgeVariant(status);
                const active = isProfileActive(profile);

                return (
                  <TableRow key={profile.id}>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {formatDisplayId(profile)}
                    </TableCell>
                    <TableCell className="min-w-[180px] font-medium">{profile.name}</TableCell>
                    <TableCell className="min-w-[160px]">{formatClient(profile)}</TableCell>
                    <TableCell className="font-mono text-sm">{formatDocument(profile)}</TableCell>
                    <TableCell className="text-sm">{formatDate(profile.created_at)}</TableCell>
                    <TableCell className="text-sm">{formatDate(profile.updated_at)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-normal">
                        {profile.technology?.name.brand ?? '-'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={badgeVariant}>{statusLabel}</Badge>
                    </TableCell>
                    <TableCell>
                      <TooltipProvider delayDuration={300}>
                        <div className="flex items-center justify-end gap-1">
                          {canViewHistory && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  aria-label="Histórico"
                                  onClick={() => handleHistory(profile)}
                                >
                                  <History className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Histórico</TooltipContent>
                            </Tooltip>
                          )}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                aria-label="Substituir Perfil"
                                disabled={!active || replaceMutation.isPending}
                                onClick={() => setReplaceProfile(profile)}
                              >
                                <RefreshCw className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Substituir Perfil</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button asChild variant="ghost" size="icon" className="h-8 w-8">
                                <Link
                                  aria-label="Editar"
                                  to={`/production/configuration-profile/form/update?id=${profile.id}`}
                                >
                                  <Edit className="h-4 w-4" />
                                </Link>
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Editar</TooltipContent>
                          </Tooltip>
                        </div>
                      </TooltipProvider>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {pages > 1 && !isLoading && (
        <div className="overflow-hidden rounded-md border">
          <Pagination
            currentPage={currentPage}
            totalPages={pages}
            totalItems={total}
            limit={limit}
            onPageChange={onPageChange ?? (() => {})}
          />
        </div>
      )}

      <Dialog open={!!historyProfile} onOpenChange={(open) => !open && setHistoryProfile(null)}>
        <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Histórico - {historyProfile?.name}</DialogTitle>
          </DialogHeader>
          {isHistoryLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Carregando histórico
            </div>
          ) : historyItems.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum registro encontrado.</p>
          ) : (
            <div className="space-y-3">
              {historyItems.map((item) => (
                <div key={item.id} className="rounded-md border p-3">
                  <div className="flex items-center justify-between gap-3">
                    <Badge variant="outline">{item.action}</Badge>
                    <span className="text-xs text-muted-foreground">
                      {formatDate(item.created_at)}
                    </span>
                  </div>
                  <p className="mt-2 text-sm">{item.description}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {item.user_name ?? item.user_email ?? item.user_id ?? 'Sistema'}
                  </p>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!replaceProfile}
        onOpenChange={(open) => !open && setReplaceProfile(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Substituir perfil</AlertDialogTitle>
            <AlertDialogDescription>
              O perfil atual será inativado e um novo perfil aguardando validação será criado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmReplace}>Substituir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
